import { startOfDay } from 'date-fns';
import type { VacationOverviewDocument } from '../../models/vacationOverview.types';
import { toIsoDay } from '../overviewDates';
import { daysUntil } from '../vacationOverview';
import { periodPhase } from './panoramaPeriodPhase';

export interface UpcomingDeparture {
	collaboratorId: string;
	name: string;
	photoUrl: string | null;
	clientNames: string[];
	startDate: string;
	endDate: string;
	days: number;
	daysToStart: number;
	returnsOn: string | null;
}

export interface TodaySnapshot {
	outToday: VacationOverviewDocument[];
	returningThisWeek: VacationOverviewDocument[];
	departingNext30: VacationOverviewDocument[];
	upcomingDepartures: UpcomingDeparture[];
	nextDeparture: UpcomingDeparture | null;
	baseCount: number;
	baseOutCount: number;
	outPercentage: number;
	returningCount: number;
	departingCount: number;
}

export function buildTodaySnapshot(
	documents: VacationOverviewDocument[],
	referenceDate: Date
): TodaySnapshot {
	const today = startOfDay(referenceDate);
	const dayIso = toIsoDay(today);
	const activeBase = documents.filter(d => d.active && d.inBase);
	const baseCount = activeBase.length;

	const outToday: VacationOverviewDocument[] = [];
	const returningThisWeek: VacationOverviewDocument[] = [];
	const departingNext30: VacationOverviewDocument[] = [];
	const upcomingDepartures: UpcomingDeparture[] = [];

	activeBase.forEach(doc => {
		const currentPeriod = doc.periods.find(p => periodPhase(p, dayIso) === 'current');

		if (currentPeriod) {
			outToday.push(doc);
			const daysToReturn = daysUntil(today, new Date(currentPeriod.endDate + 'T00:00:00'));
			if (daysToReturn >= 0 && daysToReturn <= 7) {
				returningThisWeek.push(doc);
			}
		}

		const nextPeriod = doc.periods
			.filter(p => periodPhase(p, dayIso) === 'future')
			.sort((a, b) => a.startDate.localeCompare(b.startDate))[0];

		if (!nextPeriod) return;

		const daysToStart = daysUntil(today, new Date(nextPeriod.startDate + 'T00:00:00'));
		if (daysToStart > 0 && daysToStart <= 30) {
			departingNext30.push(doc);
			upcomingDepartures.push({
				collaboratorId: doc.collaboratorId,
				name: doc.name,
				photoUrl: doc.photoUrl,
				clientNames: doc.clientNames,
				startDate: nextPeriod.startDate,
				endDate: nextPeriod.endDate,
				days: nextPeriod.days,
				daysToStart,
				returnsOn: currentPeriod?.endDate ?? null,
			});
		}
	});

	upcomingDepartures.sort((a, b) => a.daysToStart - b.daysToStart);

	const outPercentage = baseCount > 0 ? Math.round((outToday.length / baseCount) * 100) : 0;

	return {
		outToday,
		returningThisWeek,
		departingNext30,
		upcomingDepartures,
		nextDeparture: upcomingDepartures[0] ?? null,
		baseCount,
		baseOutCount: outToday.length,
		outPercentage,
		returningCount: returningThisWeek.length,
		departingCount: departingNext30.length,
	};
}
